/**
 * AdminHostDashboardSetupPage (Iter 9.40 - Pass A.12 promoted from B.12)
 *
 * `/admin/people/host-dashboards/:userId/setup`
 *
 * Lets the super-admin configure a host's dashboard BEFORE the host's profile
 * is `is_active=true` - fixes the bug "inactive dashboard cannot be viewed,
 * locking admins out of configuring it via magic links."
 *
 * Steps (left rail):
 *   1. Org link
 *   2. Category confirmation (primary + secondary)
 *   3. Branding (logo / cover URL paste)
 *   4. Services (chosen from master_services)
 *   5. Pricing tier (Free / Solo / Team / Luxe - Stripe-backed)
 *   6. Intake (universal_defaults_enabled toggle preview)
 *   7. Integrations (Acuity-only - Stripe/Beehiiv core)
 *   8. Preview
 *   9. Activate / Deactivate
 *
 * Calls
 *   GET  /api/admin/users/{id}                 - fetch user
 *   POST /api/admin/users/{id}/activate-host
 *   POST /api/admin/users/{id}/deactivate-host
 *   PATCH /api/admin/users/{id}                - update org, branding, tier
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ImageUpload from "@/components/ui/image-upload";
import { ArrowLeft, Check, ExternalLink, Loader2, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const TIERS = [
  { id: "free", label: "Free Host (legacy pilot)" },
  { id: "solo", label: "Solo (Stripe - unlimited clients)" },
  { id: "team", label: "Team (multi-host)" },
  { id: "luxe", label: "Luxe (custom branding)" },
];

const CATEGORIES = [
  "makeup", "hair", "lashes", "nails", "culinary", "wellness",
  "travel", "style", "dispensary", "events", "instructors",
];

export default function AdminHostDashboardSetupPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [primaryCat, setPrimaryCat] = useState("makeup");
  const [secondaryCats, setSecondaryCats] = useState([]);
  const [tier, setTier] = useState("free");
  const [logoUrl, setLogoUrl] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  // Iter 9.40 - host configures which preference fields they collect from clients.
  // Default = all fields for the primary category (sourced from db.categories.preference_fields).
  // Stored on db.organizations.{org_id}.host_required_fields[primary_cat] = [field_keys]
  const [allFields, setAllFields] = useState([]);            // [{key, label}]
  const [requiredFieldKeys, setRequiredFieldKeys] = useState([]);  // subset of keys
  const [magicLink, setMagicLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/${userId}/full`, { headers: auth() });
      const u = r.data?.user || r.data;
      setUser(u);
      setOrgName(u.org_name || u.business_name || u.company || u.name || "");
      const cat = u.primary_category || u.primary_category_id || "makeup";
      setPrimaryCat(cat);
      setSecondaryCats(u.secondary_category_ids || u.secondary_categories || []);
      setTier(u.subscription_tier || u.tier || (u.is_pilot ? "free" : "solo"));
      setLogoUrl(u.org_logo_url || "");
      setCoverUrl(u.org_cover_url || "");

      // Iter 9.40 - load category schema + the host's already-saved required-fields subset
      try {
        const sR = await axios.get(`${API}/me/preferences/schema`);
        const catDoc = (sR.data?.categories || []).find((c) => c.category_id === cat);
        const fields = (catDoc?.preference_fields || [])
          .filter((f) => f.key !== "notes")
          .map((f) => ({ key: f.key, label: f.label }));
        setAllFields(fields);

        // Saved subset on user.host_required_fields[cat] (or default = all)
        const saved = (u.host_required_fields && u.host_required_fields[cat]) || null;
        setRequiredFieldKeys(saved || fields.map((f) => f.key));
      } catch { /* non-blocking */ }

      // Pull magic link (so admin can preview as the host)
      try {
        const m = await axios.post(
          `${API}/admin/users/${userId}/magic-link`,
          {}, { headers: auth() }
        );
        if (m.data?.url || m.data?.magic_link_url) {
          setMagicLink(m.data.url || m.data.magic_link_url);
        }
      } catch { /* non-blocking */ }
    } catch (e) {
      toast.error("Could not load user");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const isActive = user?.is_host && (user?.host_categories || []).includes(primaryCat);
  const dashboardUrl = (() => {
    const base = `/host/v2/evt_${(userId || "").replace(/^usr_/, "").slice(0, 16)}_${primaryCat}`;
    return base;
  })();

  const persistConfig = async () => {
    setBusy(true);
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        {
          org_name: orgName,
          primary_category: primaryCat,
          secondary_category_ids: secondaryCats,
          subscription_tier: tier,
          org_logo_url: logoUrl,
          org_cover_url: coverUrl,
          // Iter 9.40 - persist host's required preference field subset
          // Stored as user.host_required_fields[cat] = [field_keys]
          host_required_fields: {
            ...(user?.host_required_fields || {}),
            [primaryCat]: requiredFieldKeys,
          },
        },
        { headers: auth() }
      );
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/users/${userId}/activate-host`,
        { category: primaryCat, org_name: orgName, plan_tier: tier },
        { headers: auth() }
      );
      toast.success("Host dashboard activated");
      load();
    } catch (e) {
      toast.error(`Activation failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/users/${userId}/deactivate-host`,
        { category: primaryCat },
        { headers: auth() }
      );
      toast.success("Host dashboard deactivated (data preserved)");
      load();
    } catch (e) {
      toast.error(`Deactivation failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-host-setup-page">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link to="/admin/users" className="text-xs text-gray-500 inline-flex items-center gap-1 hover:text-black">
          <ArrowLeft className="w-3 h-3" /> Back to People
        </Link>
        <div className="flex items-center justify-between mt-2 mb-4">
          <div>
            <h1 className="text-2xl font-semibold">
              Host Dashboard Setup
            </h1>
            <p className="text-sm text-gray-500">
              {user?.first_name} {user?.last_name} ·{" "}
              <span className="text-gray-700">{user?.email}</span>
            </p>
          </div>
          <Badge
            variant="outline"
            className={isActive ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}
            data-testid="host-setup-active-badge"
          >
            {isActive ? "Active" : "Draft / not active"}
          </Badge>
        </div>

        <Card className="border-gray-200 mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">1. Org link</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="e.g. Privé Beauté"
              data-testid="host-setup-org-name"
            />
          </CardContent>
        </Card>

        <Card className="border-gray-200 mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">2. Category</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <div className="text-[11px] text-gray-500 mb-1">Primary</div>
              <Select value={primaryCat} onValueChange={setPrimaryCat}>
                <SelectTrigger data-testid="host-setup-primary-cat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[11px] text-gray-500 mb-1">Secondary (multi)</div>
              <div className="flex flex-wrap gap-1">
                {CATEGORIES.filter((c) => c !== primaryCat).map((c) => {
                  const sel = secondaryCats.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setSecondaryCats((cs) => sel ? cs.filter((x) => x !== c) : [...cs, c])}
                      className={`text-xs px-2 py-1 rounded border ${sel ? "bg-black text-white border-black" : "bg-white border-gray-200"}`}
                      data-testid={`host-setup-secondary-${c}`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200 mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">3. Branding</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ImageUpload
              label="Logo"
              value={logoUrl}
              onChange={setLogoUrl}
              previewSize="medium"
              aspectRatio="square"
              description="Square logo, shown on host header"
            />
            <ImageUpload
              label="Cover"
              value={coverUrl}
              onChange={setCoverUrl}
              previewSize="banner"
              aspectRatio="banner"
              description="Wide banner, top of host dashboard"
            />
          </CardContent>
        </Card>

        <Card className="border-gray-200 mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">4. Pricing tier</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger data-testid="host-setup-tier"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIERS.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-400 mt-2">
              Stripe + Beehiiv are core systems and can't be toggled. Acuity is the
              only optional integration; configure on the host's dashboard once active.
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 mb-4" data-testid="host-setup-required-fields-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">5. Required preference fields</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[11px] text-gray-500 mb-2">
              Choose which preference fields this host collects from clients for{" "}
              <strong className="text-gray-700">{primaryCat}</strong>. The client's /me
              still stores everything; only the selected fields auto-fill the
              appointment intake. Default = all.
            </p>
            {allFields.length === 0 ? (
              <p className="text-[11px] text-gray-400 italic">
                No preference fields seeded for this category yet.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {allFields.map((f) => {
                  const sel = requiredFieldKeys.includes(f.key);
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() =>
                        setRequiredFieldKeys((cur) =>
                          sel ? cur.filter((x) => x !== f.key) : [...cur, f.key]
                        )
                      }
                      className={`text-xs px-2 py-1 rounded border ${
                        sel
                          ? "bg-black text-white border-black"
                          : "bg-white border-gray-200 text-gray-700"
                      }`}
                      data-testid={`host-setup-field-${f.key}`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="text-[10px] text-gray-400 mt-2">
              Selected: {requiredFieldKeys.length}/{allFields.length}
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200 mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">6. Preview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <a
              href={dashboardUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
              data-testid="host-setup-preview-link"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open host dashboard ({dashboardUrl})
            </a>
            {magicLink && (
              <div className="text-[11px] text-gray-500">
                Magic-link as host: <span className="font-mono break-all">{magicLink}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center gap-2 sticky bottom-0 bg-white border-t pt-3">
          <Button
            onClick={persistConfig}
            disabled={busy}
            variant="outline"
            data-testid="host-setup-save"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Save config
          </Button>
          {isActive ? (
            <Button
              onClick={deactivate}
              disabled={busy}
              variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50"
              data-testid="host-setup-deactivate"
            >
              <PowerOff className="w-4 h-4 mr-2" /> Deactivate
            </Button>
          ) : (
            <Button
              onClick={activate}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="host-setup-activate"
            >
              <Power className="w-4 h-4 mr-2" /> Activate
            </Button>
          )}
          <span className="ml-auto text-[11px] text-gray-400">
            Deactivation preserves all data. Reactivation restores instantly.
          </span>
        </div>
      </div>
    </div>
  );
}
